<template>
  <el-dialog
    :visible.sync="dialogVisible"
    :close-on-click-modal="false"
    :append-to-body="true"
    custom-class="labelling-guide-dialog"
    top="5vh"
    width="min(1120px, 92vw)"
  >
    <div slot="title" class="guide-dialog-title">
      <div>
        <div class="guide-title-text">Labelling Guide & Instructions</div>
      </div>
    </div>

    <div class="guide-dialog-body">
      <div class="guide-hero-card">
        <div>
          <p>
            Hello team, your task is to inspect the time-series line charts
            tracking two sensors - S1(blue) and S2(green) and identify regions
            where the data is corrupt, erratic and unreliable.
          </p>
          <p>
            Please use this guide to understand what constitutes a good vs bad
            signal, how to execute the workflow and how to use this web
            application interface.
          </p>
        </div>
      </div>

      <el-tabs v-model="activeGuideTab" class="guide-tabs">
        <el-tab-pane label="Workflow" name="workflow">
          <div class="guide-tab-panel">
            <div class="guide-section-copy">
              <p>Follow these points for every chart presented to you:</p>
            </div>

            <div class="workflow-steps">
              <div
                v-for="step in guideWorkflowSteps"
                :key="step.id"
                class="workflow-step-card"
              >
                <div class="workflow-step-number">{{ step.id }}</div>
                <div class="workflow-step-content">
                  <div class="workflow-step-header">
                    <div class="workflow-step-title">{{ step.title }}</div>

                    <div
                      v-if="step.links && step.links.length"
                      class="workflow-step-links"
                    >
                      <el-button
                        v-for="link in step.links"
                        :key="link.tab"
                        type="text"
                        @click="openGuideTab(link.tab)"
                      >
                        {{ link.label }}
                      </el-button>
                    </div>
                  </div>

                  <p>{{ step.description }}</p>

                  <ul
                    v-if="step.checklist && step.checklist.length"
                    class="guide-bullet-list"
                  >
                    <li v-for="item in step.checklist" :key="item">
                      {{ item }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </el-tab-pane>

        <el-tab-pane label="GOOD Signals" name="good-signals">
          <div class="guide-tab-panel">
            <div class="guide-section-copy">
              <p>
                A healthy session does not need to be perfectly flat / rising,
                but it must exhibit stable, continuous behaviour.
              </p>
            </div>

            <div class="signal-rule-grid mb-20">
              <div
                v-for="rule in goodSignalRules"
                :key="rule.title"
                class="signal-rule-card good"
              >
                <div class="signal-rule-title">{{ rule.title }}</div>
                <p>{{ rule.description }}</p>
              </div>
            </div>

            <div class="sample-section-header">
              <div>
                <h4>GOOD sample sessions</h4>
                <p>
                  These examples are acceptable and should usually be submitted
                  as clean unless another sustained issue appears elsewhere in
                  the same cycle.
                </p>
              </div>
            </div>

            <div class="sample-grid">
              <article
                v-for="(sample, sampleIndex) in goodSignalSamples"
                :key="sample.src"
                class="sample-card"
              >
                <el-image
                  :src="sample.src"
                  :preview-src-list="goodSignalPreviewList"
                  :initial-index="sampleIndex"
                  :alt="sample.title"
                  class="sample-image"
                  fit="cover"
                />
                <div class="sample-card-body">
                  <div class="sample-card-title">{{ sample.title }}</div>
                </div>
              </article>
            </div>
          </div>
        </el-tab-pane>

        <el-tab-pane label="BAD Signals" name="bad-signals">
          <div class="guide-tab-panel">
            <div class="guide-section-copy">
              <p>
                A Bad Signals represent noise, poor contact and malfunctions.
              </p>
              <p>
                You must drag a box over the region and label it with one of
                these specific reason codes:
              </p>
            </div>

            <div class="signal-rule-grid mb-20">
              <div
                v-for="rule in badSignalRules"
                :key="rule.title"
                :class="[
                  'signal-rule-card bad',
                  { selected: selectedBadSignalRule === rule.title }
                ]"
                @click="selectBadSignalRule(rule.title)"
              >
                <div class="signal-rule-title">{{ rule.title }}</div>
                <p>{{ rule.description }}</p>
              </div>
            </div>

            <div class="sample-section-header">
              <div>
                <h4>BAD sample sessions</h4>
                <p>
                  Draw a region over the exact corrupted window, choose the
                  affected sensor, and assign the matching reason code.
                </p>
              </div>
            </div>

            <div class="sample-grid sample-grid-wide">
              <article
                v-for="(sample, sampleIndex) in badSignalSamples"
                :key="sample.src"
                class="sample-card"
              >
                <el-image
                  :src="sample.src"
                  :preview-src-list="badSignalPreviewList"
                  :initial-index="sampleIndex"
                  :alt="sample.title"
                  class="sample-image"
                  fit="cover"
                />
                <div class="sample-card-body">
                  <div class="sample-card-title">{{ sample.title }}</div>
                </div>
              </article>
            </div>

            <div class="overlay-guide-card mt-20">
              <div class="sample-section-header">
                <div>
                  <h4>Overlay color mapping</h4>
                  <p>
                    The selection color changes after choosing the target
                    sensor. Use it as a quick visual confirmation before you
                    submit.
                  </p>
                </div>
              </div>

              <div class="overlay-legend">
                <div
                  v-for="item in guideOverlayLegend"
                  :key="item.label"
                  class="overlay-legend-item"
                >
                  <span
                    class="overlay-legend-swatch"
                    :style="{
                      background: item.fill,
                      borderColor: item.border
                    }"
                  ></span>
                  <div>
                    <div class="overlay-legend-title">{{ item.label }}</div>
                    <div class="overlay-legend-caption">
                      {{ item.caption }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </el-tab-pane>
      </el-tabs>
    </div>
  </el-dialog>
</template>

<script>
const GOOD_SESSION_1 = require('@/assets/labelling-guide/good-session-1.png')
const GOOD_SESSION_2 = require('@/assets/labelling-guide/good-session-2.png')
const GOOD_SESSION_3 = require('@/assets/labelling-guide/good-session-3.png')
const GOOD_SESSION_4 = require('@/assets/labelling-guide/good-session-4.png')
const GOOD_SESSION_5 = require('@/assets/labelling-guide/good-session-5.png')
const GOOD_SESSION_6 = require('@/assets/labelling-guide/good-session-6.png')
const GOOD_SESSION_7 = require('@/assets/labelling-guide/good-session-7.png')
const GOOD_SESSION_8 = require('@/assets/labelling-guide/good-session-8.png')
const GOOD_SESSION_9 = require('@/assets/labelling-guide/good-session-9.png')
const BAD_SESSION_1 = require('@/assets/labelling-guide/bad-session-1.png')
const BAD_SESSION_2 = require('@/assets/labelling-guide/bad-session-2.png')
const BAD_SESSION_3 = require('@/assets/labelling-guide/bad-session-3.png')
const BAD_SESSION_4 = require('@/assets/labelling-guide/bad-session-4.png')
const PAD_STARTED_WET_SESSION_1 = require('@/assets/labelling-guide/pad-started-wet-session-1.png')

const GUIDE_WORKFLOW_STEPS = [
  {
    id: '1',
    title: 'Evaluate the entire chart',
    description:
      'Look from left to right along the X-axis time duration to assess the overall health of S1 and S2.',
    checklist: []
  },
  {
    id: '2',
    title: 'Check the Y-axis',
    description:
      'Keep an eye out for values exceeding 200 at the start or scaling past 2000.',
    checklist: []
  },
  {
    id: '3',
    title: 'Scenario A: The chart is healthy',
    description: '',
    checklist: [
      'Click "Sensor values looks clean throughout the pad cycle" checkbox under the chart.',
      'Click the "Submit" button.'
    ],
    links: [
      {
        label: 'Show Good Signals',
        tab: 'good-signals'
      }
    ]
  },
  {
    id: '4',
    title: 'Scenario B : The Chart Contains Bad signals',
    description: '',
    checklist: [
      'Position your mouse cursor at the exact time where the bad signal or noise begins.',
      'Click and drag horizontally across the chart to overlay the box covering the entire bad section.',
      'Select the Target Sensor: In the popup menu, choose whether the anomaly applies to S1, S2 or Both.',
      'Select the correct reason code(Pad started wet or Excessive Noise/Erratic readings).',
      'Add notes(Optional)'
    ],
    links: [
      {
        label: 'Show Bad Signals',
        tab: 'bad-signals'
      }
    ]
  },
  {
    id: '5',
    title: 'Verify the Overlay Color',
    description:
      'Once selected, the box will change its distinct color mapping.',
    checklist: [
      'S1 Selection Overlay: Light Blue color',
      'S2 Selection Overlay: Light Green color',
      'Both Selection Overlay: Light Brown color'
    ],
    links: [
      {
        label: 'See overlay colors in BAD Signals',
        tab: 'bad-signals'
      }
    ]
  },
  {
    id: '6',
    title: 'Review & Adjust',
    description:
      'If there are multiple separate bad regions in the same session, repeat the drag-and-select action for each window. If you made an error while dragging, you can adjust by moving the box / adjusting the width of the box. Additionally, you can remove the box by clicking the small "x" button in the top right corner of the box to delete it and try again.',
    checklist: []
  },
  {
    id: '7',
    title: 'Final Submission',
    description:
      'Once all bad regions are successfully highlighted, click the submit button.',
    checklist: []
  }
]

const GOOD_SIGNAL_RULES = [
  {
    title: 'Perfect Session',
    description:
      'A session shows a clear upward curve or rise in both S1 and S2. During this rise, S1(Blue) will stay relatively lower than S2(Green). It is perfectly normal for S2 to trend upwards while S1 remains stable.'
  },
  {
    title: 'Dry diaper',
    description:
      'If both lines are flat and steady, this is completely normal and expected. It demonstrates a dry diaper.'
  },
  {
    title: 'Acceptable Micro-spikes',
    description:
      'Minor, single point sharp drops or spikes are normal. Do not mark these as bad data.'
  },
  {
    title: 'Initialization/Termination',
    description:
      'Few sharp rises /drops during the start and the end of the session are normal and are considered clean data.'
  }
]

const BAD_SIGNAL_RULES = [
  {
    title: 'Pad started wet',
    description:
      'A normal baseline must be below 200 for both sensor lines. If either sensor line reads above 200 for a prolonged period of time right from the start(excluding few momentary spikes), it is a bad signal.'
  },
  {
    title: 'Excessive Noise/Erratic readings',
    description:
      'A few scattered drops and rises are fine. However, if fluctuations continue over a long period of time, it is a bad signal.'
  }
]

const GUIDE_OVERLAY_LEGEND = [
  {
    label: 'S1 Selection Overlay',
    caption: 'Light Blue color',
    fill: 'rgba(0, 143, 251, 0.24)',
    border: '#008ffb'
  },
  {
    label: 'S2 Selection Overlay',
    caption: 'Light Green color',
    fill: 'rgba(0, 227, 150, 0.24)',
    border: '#00e396'
  },
  {
    label: 'Both Selection Overlay',
    caption: 'Light Brown color',
    fill: 'rgba(245, 158, 11, 0.24)',
    border: '#f59e0b'
  }
]

const GOOD_SIGNAL_SAMPLES = [
  {
    title: 'GOOD example 1',
    src: GOOD_SESSION_1
  },
  {
    title: 'GOOD example 2',
    src: GOOD_SESSION_2
  },
  {
    title: 'GOOD example 3',
    src: GOOD_SESSION_3
  },
  {
    title: 'GOOD example 4',
    src: GOOD_SESSION_4
  },
  {
    title: 'GOOD example 5',
    src: GOOD_SESSION_5
  },
  {
    title: 'GOOD example 6',
    src: GOOD_SESSION_6
  },
  {
    title: 'GOOD example 7',
    src: GOOD_SESSION_7
  },
  {
    title: 'GOOD example 8',
    src: GOOD_SESSION_8
  },
  {
    title: 'GOOD example 9',
    src: GOOD_SESSION_9
  }
]

const BAD_SIGNAL_SAMPLE_GROUPS = {
  'Pad started wet': [
    {
      title: 'BAD example 1',
      src: PAD_STARTED_WET_SESSION_1
    }
  ],
  'Excessive Noise/Erratic readings': [
    {
      title: 'BAD example 1',
      src: BAD_SESSION_1
    },
    {
      title: 'BAD example 2',
      src: BAD_SESSION_2
    },
    {
      title: 'BAD example 3',
      src: BAD_SESSION_3
    },
    {
      title: 'BAD example 4',
      src: BAD_SESSION_4
    }
  ]
}

export default {
  name: 'LabellingGuideDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    sensorOptions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      activeGuideTab: 'workflow',
      guideWorkflowSteps: GUIDE_WORKFLOW_STEPS,
      goodSignalRules: GOOD_SIGNAL_RULES,
      badSignalRules: BAD_SIGNAL_RULES,
      selectedBadSignalRule: '',
      guideOverlayLegend: GUIDE_OVERLAY_LEGEND,
      goodSignalSamples: GOOD_SIGNAL_SAMPLES
    }
  },
  computed: {
    goodSignalPreviewList() {
      return this.goodSignalSamples.map(sample => sample.src)
    },
    badSignalSamples() {
      if (!this.selectedBadSignalRule) {
        return Object.values(BAD_SIGNAL_SAMPLE_GROUPS).reduce(
          (samples, sampleGroup) => samples.concat(sampleGroup),
          []
        )
      }

      return BAD_SIGNAL_SAMPLE_GROUPS[this.selectedBadSignalRule] || []
    },
    badSignalPreviewList() {
      return this.badSignalSamples.map(sample => sample.src)
    },
    dialogVisible: {
      get() {
        return this.visible
      },
      set(value) {
        this.$emit('update:visible', value)
      }
    }
  },
  watch: {
    visible(nextValue) {
      if (nextValue) {
        this.activeGuideTab = 'workflow'
      }
    }
  },
  methods: {
    openGuideTab(tabName) {
      this.activeGuideTab = tabName
    },
    selectBadSignalRule(ruleTitle) {
      this.selectedBadSignalRule = ruleTitle
    }
  }
}
</script>

<style lang="scss" scoped>
/deep/ .labelling-guide-dialog {
  border-radius: 24px;
  overflow: hidden;
  background: #f8fbff;

  .el-dialog__header {
    padding: 24px 28px 0;
  }

  .el-dialog__body {
    padding: 20px 28px 28px;
  }
}

/deep/ .el-image-viewer__close {
  top: 24px;
  right: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  border: 1px solid rgba(255, 255, 255, 0.24);
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.82);
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.34);
  color: #ffffff;
  opacity: 1;
  transition: background-color 0.2s ease, transform 0.2s ease,
    box-shadow 0.2s ease;
}

/deep/ .el-image-viewer__close i {
  font-size: 22px;
  font-weight: 700;
}

/deep/ .el-image-viewer__close:hover {
  background: rgba(15, 23, 42, 0.96);
  box-shadow: 0 16px 32px rgba(15, 23, 42, 0.4);
  transform: scale(1.04);
}

/deep/ .el-image-viewer__close:focus-visible {
  outline: 2px solid #ffffff;
  outline-offset: 3px;
}

.guide-title-text {
  font-size: 22px;
  font-weight: 700;
  color: #0f172a;
}

.guide-dialog-body {
  display: grid;
  gap: 18px;
}

.guide-hero-card {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 18px;
  align-items: center;
  padding: 20px 22px;
  border: 1px solid #d8e4f1;
  border-radius: 20px;
  background: linear-gradient(135deg, #eff6ff, #f8fafc 62%, #ffffff);

  h3 {
    margin: 6px 0 8px;
    font-size: 22px;
    line-height: 1.35;
    color: #0f172a;
  }

  p {
    margin: 0;
    color: #475569;
    line-height: 1.6;
  }
}

.guide-tabs {
  /deep/ .el-tabs__header {
    margin-bottom: 12px;
  }

  /deep/ .el-tabs__nav-wrap::after {
    background: #dbe5ee;
  }

  /deep/ .el-tabs__item {
    font-weight: 600;
  }
}

.guide-tab-panel {
  max-height: 62vh;
  overflow-y: auto;
  padding-right: 4px;
}

.guide-section-copy {
  margin-bottom: 14px;

  p {
    margin: 0;
    color: #52607a;
    line-height: 1.6;
  }

  p + p {
    margin-top: 8px;
  }
}

.workflow-steps {
  display: grid;
  gap: 14px;
}

.workflow-step-card {
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr);
  gap: 14px;
  padding: 18px;
  border: 1px solid #dbe5ee;
  border-radius: 18px;
  background: #fff;
}

.workflow-step-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: #0f172a;
  color: #fff;
  font-size: 18px;
  font-weight: 700;
}

.workflow-step-title,
.signal-rule-title,
.sample-card-title,
.reason-code-title,
.overlay-legend-title {
  font-weight: 700;
  color: #0f172a;
}

.workflow-step-content p,
.signal-rule-card p,
.sample-card-body p,
.reason-code-card p {
  margin: 8px 0 0;
  color: #52607a;
  line-height: 1.6;
}

.workflow-step-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.workflow-step-links {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  .el-button {
    padding: 0;
  }
}

.guide-bullet-list {
  margin: 10px 0 0;
  padding-left: 18px;
  color: #334155;

  li + li {
    margin-top: 6px;
  }
}

.signal-rule-grid,
.reason-code-grid,
.sample-grid {
  display: grid;
  gap: 14px;
}

.signal-rule-grid,
.reason-code-grid {
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}

.signal-rule-card,
.reason-code-card,
.overlay-guide-card,
.sample-card {
  border: 1px solid #dbe5ee;
  border-radius: 18px;
  background: #fff;
}

.signal-rule-card,
.reason-code-card {
  padding: 18px;
}

.signal-rule-card.good {
  background: linear-gradient(180deg, #f0fdf4, #ffffff);
}

.signal-rule-card.bad {
  background: linear-gradient(180deg, #fff1f2, #ffffff);
}

.signal-rule-card.bad.selected {
  border-color: #fb7185;
  box-shadow: 0 0 0 2px rgba(251, 113, 133, 0.16);
}

.sample-section-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;

  h4 {
    margin: 0;
    font-size: 18px;
    color: #0f172a;
  }

  p {
    margin: 6px 0 0;
    color: #52607a;
    line-height: 1.6;
  }
}

.sample-grid {
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
}

.sample-grid-wide {
  grid-template-columns: repeat(auto-fill, minmax(320px, 320px));
}

.sample-image {
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-bottom: 1px solid #dbe5ee;
  background: #0f172a;
  cursor: zoom-in;
}

.sample-card-body {
  padding: 14px 16px 16px;
}

.mt-20 {
  margin-top: 20px;
}

.overlay-guide-card {
  padding: 18px;
}

.overlay-legend {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 14px;
}

.overlay-legend-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  border-radius: 16px;
  background: #f8fafc;
}

.overlay-legend-swatch {
  width: 42px;
  height: 42px;
  border: 2px solid transparent;
  border-radius: 12px;
  flex: 0 0 42px;
}

.overlay-legend-caption {
  margin-top: 4px;
  color: #52607a;
  line-height: 1.5;
}

@media (max-width: 767px) {
  .guide-hero-card,
  .sample-section-header,
  .workflow-step-card {
    grid-template-columns: 1fr;
  }

  .workflow-step-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .guide-tab-panel {
    max-height: 58vh;
  }
}
</style>
